// TelcoTrack — strom kategorií zařízení + vlastní specifikace per kategorie

/**
 * Režim přiřazení pro všechna zařízení v kategorii.
 * - `reservable` — dočasná rezervace na dobu (kalendář / detail)
 * - `permanent` — trvalé přiřazení držiteli (předávací protokol); rezervace zakázána
 */
export const EQUIPMENT_ASSIGNMENT_MODES = ['reservable', 'permanent'] as const;

export type EquipmentAssignmentMode = (typeof EQUIPMENT_ASSIGNMENT_MODES)[number];

export const EQUIPMENT_ASSIGNMENT_MODE_LABELS: Record<EquipmentAssignmentMode, string> = {
  reservable: 'Lze rezervovat',
  permanent: 'Trvalé přiřazení',
};

/** Výchozí režim (zpětná kompatibilita existujících kategorií). */
export const EQUIPMENT_ASSIGNMENT_MODE_DEFAULT: EquipmentAssignmentMode = 'reservable';

export function isEquipmentAssignmentMode(v: unknown): v is EquipmentAssignmentMode {
  return (
    typeof v === 'string' &&
    (EQUIPMENT_ASSIGNMENT_MODES as readonly string[]).includes(v)
  );
}

/** Normalizace vstupu; neplatné / prázdné → výchozí `reservable`. */
export function normalizeEquipmentAssignmentMode(raw: unknown): EquipmentAssignmentMode {
  if (isEquipmentAssignmentMode(raw)) return raw;
  return EQUIPMENT_ASSIGNMENT_MODE_DEFAULT;
}

/** Typy hodnot vlastních specifikací zařízení (per kategorie). */
export const EQUIPMENT_CUSTOM_SPEC_FIELD_TYPES = [
  'text',
  'number',
  'date',
  'boolean',
] as const;

export type EquipmentCustomSpecFieldType =
  (typeof EQUIPMENT_CUSTOM_SPEC_FIELD_TYPES)[number];

export const EQUIPMENT_CUSTOM_SPEC_FIELD_TYPE_LABELS: Record<
  EquipmentCustomSpecFieldType,
  string
> = {
  text: 'Text',
  number: 'Číslo',
  date: 'Datum',
  boolean: 'Ano / Ne',
};

/** Definice jednoho vlastního pole na kategorii zařízení. */
export interface EquipmentCustomSpecFieldDef {
  /** Stabilní klíč (slug) — hodnoty v `equipment.custom_spec_values`. */
  key: string;
  /** Český popisek ve formuláři / detailu. */
  labelCs: string;
  type: EquipmentCustomSpecFieldType;
  required?: boolean;
  /** Volitelné možnosti pro výběr (typ text); prázdné = volný text. */
  options?: string[];
  /** Pořadí zobrazení (vzestupně). */
  sortOrder: number;
}

export type EquipmentCustomSpecValue = string | number | boolean | null;

/** Hodnoty vlastních specifikací na instanci zařízení (klíč = field.key). */
export type EquipmentCustomSpecValues = Record<string, EquipmentCustomSpecValue>;

export interface EquipmentCategoryDto {
  id: string;
  name: string;
  parentId: string | null;
  sortOrder: number;
  /**
   * Režim přiřazení jednotek v této kategorii
   * (`reservable` = dočasná rezervace, `permanent` = trvalé předání).
   */
  assignmentMode: EquipmentAssignmentMode;
  /** Schéma vlastních specifikací pro zařízení v této kategorii. */
  customSpecFields: EquipmentCustomSpecFieldDef[];
}

export interface EquipmentCategoryCreateInput {
  name: string;
  parentId?: string | null;
  sortOrder?: number;
  assignmentMode?: EquipmentAssignmentMode;
  customSpecFields?: EquipmentCustomSpecFieldDef[];
}

export interface EquipmentCategoryUpdateInput {
  name?: string;
  parentId?: string | null;
  sortOrder?: number;
  assignmentMode?: EquipmentAssignmentMode;
  customSpecFields?: EquipmentCustomSpecFieldDef[];
}

/** Výchozí specifikace kategorie Vozidlo (seed / dokumentace). */
export const EQUIPMENT_VEHICLE_CATEGORY_NAME = 'Vozidlo';

export const EQUIPMENT_VEHICLE_DEFAULT_SPEC_FIELDS: EquipmentCustomSpecFieldDef[] = [
  { key: 'spz', labelCs: 'SPZ', type: 'text', required: false, sortOrder: 10 },
  { key: 'vin', labelCs: 'VIN', type: 'text', required: false, sortOrder: 20 },
  {
    key: 'stk_valid_until',
    labelCs: 'Platnost STK do',
    type: 'date',
    required: false,
    sortOrder: 30,
  },
  {
    key: 'year_of_manufacture',
    labelCs: 'Rok výroby',
    type: 'number',
    required: false,
    sortOrder: 40,
  },
  {
    key: 'last_oil_change',
    labelCs: 'Poslední výměna oleje',
    type: 'date',
    required: false,
    sortOrder: 50,
  },
  {
    key: 'tire_size',
    labelCs: 'Rozměr pneu',
    type: 'text',
    required: false,
    sortOrder: 60,
  },
  {
    key: 'vignette_valid_until',
    labelCs: 'Dálniční známka (platnost do)',
    type: 'date',
    required: false,
    sortOrder: 70,
  },
  {
    key: 'green_card_valid_until',
    labelCs: 'Zelená karta (platnost do)',
    type: 'date',
    required: false,
    sortOrder: 80,
  },
  {
    key: 'insurance_valid_until',
    labelCs: 'Pojištění (platnost do)',
    type: 'date',
    required: false,
    sortOrder: 90,
  },
];

const KEY_RE = /^[a-z][a-z0-9_]{0,63}$/;

/** Složí stabilní klíč (a–z, 0–9, _) z českého popisku. */
export function slugEquipmentCustomSpecKey(label: string): string {
  const folded = String(label ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
  let slug = folded
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (!slug) return 'pole';
  if (!/^[a-z]/.test(slug)) slug = `p_${slug}`;
  return slug.slice(0, 64);
}

/** Unikátní klíč vůči již použitým (nová pole; existující klíče se nemění). */
export function allocateEquipmentCustomSpecKey(label: string, used: ReadonlySet<string>): string {
  const base = slugEquipmentCustomSpecKey(label);
  if (!used.has(base)) return base;
  for (let n = 2; n < 1000; n++) {
    const suffix = `_${n}`;
    const candidate = `${base.slice(0, 64 - suffix.length)}${suffix}`;
    if (!used.has(candidate)) return candidate;
  }
  const fallback = `pole_${used.size + 1}`;
  return fallback.slice(0, 64);
}

export function isEquipmentCustomSpecFieldType(
  v: unknown,
): v is EquipmentCustomSpecFieldType {
  return (
    typeof v === 'string' &&
    (EQUIPMENT_CUSTOM_SPEC_FIELD_TYPES as readonly string[]).includes(v)
  );
}

/** Seřadí pole podle sortOrder, pak labelCs. */
export function sortEquipmentCustomSpecFields(
  fields: EquipmentCustomSpecFieldDef[],
): EquipmentCustomSpecFieldDef[] {
  return [...fields].sort((a, b) => {
    const so = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    if (so !== 0) return so;
    return String(a.labelCs ?? '').localeCompare(String(b.labelCs ?? ''), 'cs');
  });
}

/**
 * Normalizuje a validuje seznam definic polí kategorie.
 * Vyhodí Error s česky srozumitelnou hláškou při neplatném vstupu.
 */
export function normalizeEquipmentCustomSpecFields(
  raw: unknown,
): EquipmentCustomSpecFieldDef[] {
  if (raw == null) return [];
  if (!Array.isArray(raw)) {
    throw new Error('Vlastní specifikace musí být seznam polí');
  }
  const seen = new Set<string>();
  const out: EquipmentCustomSpecFieldDef[] = [];
  for (let i = 0; i < raw.length; i++) {
    const item = raw[i];
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      throw new Error(`Neplatná definice pole #${i + 1}`);
    }
    // Named props (not Record index signature) so Angular
    // `noPropertyAccessFromIndexSignature` allows dot access.
    const rec = item as {
      key?: unknown;
      labelCs?: unknown;
      type?: unknown;
      sortOrder?: unknown;
      options?: unknown;
      required?: unknown;
    };
    const key = String(rec.key ?? '')
      .trim()
      .toLowerCase();
    if (!KEY_RE.test(key)) {
      throw new Error(
        `Klíč pole „${rec.key ?? ''}“ musí začínat písmenem a obsahovat jen a–z, 0–9, _ (max 64)`,
      );
    }
    if (seen.has(key)) {
      throw new Error(`Duplicitní klíč pole „${key}“`);
    }
    seen.add(key);
    const labelCs = String(rec.labelCs ?? '').trim();
    if (!labelCs) {
      throw new Error(`Pole „${key}“ musí mít český popisek`);
    }
    if (!isEquipmentCustomSpecFieldType(rec.type)) {
      throw new Error(`Pole „${key}“: neplatný typ`);
    }
    const sortOrder =
      rec.sortOrder != null && Number.isFinite(Number(rec.sortOrder))
        ? Number(rec.sortOrder)
        : (i + 1) * 10;
    let options: string[] | undefined;
    if (Array.isArray(rec.options)) {
      options = rec.options
        .map((o) => String(o ?? '').trim())
        .filter((o) => o.length > 0);
      if (!options.length) options = undefined;
    }
    out.push({
      key,
      labelCs,
      type: rec.type,
      required: !!rec.required,
      ...(options ? { options } : {}),
      sortOrder,
    });
  }
  return sortEquipmentCustomSpecFields(out);
}

function isBlankSpecValue(v: EquipmentCustomSpecValue | undefined): boolean {
  if (v == null) return true;
  if (typeof v === 'string') return v.trim() === '';
  return false;
}

/**
 * Normalizuje hodnoty podle schématu kategorie.
 * - neznámé klíče se zahodí (orphan z předchozí kategorie se nevrací)
 * - povinná prázdná pole → Error
 * - boolean / number / date validace
 */
export function normalizeEquipmentCustomSpecValues(
  fields: EquipmentCustomSpecFieldDef[],
  raw: unknown,
  opts?: { requireRequired?: boolean },
): EquipmentCustomSpecValues {
  const requireRequired = opts?.requireRequired !== false;
  const src =
    raw && typeof raw === 'object' && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {};
  const out: EquipmentCustomSpecValues = {};
  for (const field of sortEquipmentCustomSpecFields(fields)) {
    const hasKey = Object.prototype.hasOwnProperty.call(src, field.key);
    const rawVal = hasKey ? src[field.key] : undefined;
    const normalized = coerceSpecValue(field, rawVal);
    if (requireRequired && field.required && isBlankSpecValue(normalized)) {
      throw new Error(`Vyplňte povinné pole „${field.labelCs}“`);
    }
    if (!isBlankSpecValue(normalized)) {
      out[field.key] = normalized;
    } else if (hasKey) {
      out[field.key] = null;
    }
  }
  return out;
}

function coerceSpecValue(
  field: EquipmentCustomSpecFieldDef,
  raw: unknown,
): EquipmentCustomSpecValue {
  if (raw == null || raw === '') return null;
  switch (field.type) {
    case 'boolean': {
      if (typeof raw === 'boolean') return raw;
      if (raw === 'true' || raw === 1 || raw === '1') return true;
      if (raw === 'false' || raw === 0 || raw === '0') return false;
      throw new Error(`Pole „${field.labelCs}“ musí být ano/ne`);
    }
    case 'number': {
      const n = typeof raw === 'number' ? raw : Number(String(raw).trim());
      if (!Number.isFinite(n)) {
        throw new Error(`Pole „${field.labelCs}“ musí být číslo`);
      }
      return n;
    }
    case 'date': {
      const s = String(raw).trim();
      // YYYY-MM-DD or ISO datetime → date part
      const m = /^(\d{4}-\d{2}-\d{2})/.exec(s);
      if (!m) {
        throw new Error(`Pole „${field.labelCs}“ musí být datum (RRRR-MM-DD)`);
      }
      return m[1];
    }
    case 'text':
    default: {
      const s = String(raw).trim();
      if (field.options?.length && !field.options.includes(s)) {
        throw new Error(
          `Pole „${field.labelCs}“: hodnota není mezi povolenými možnostmi`,
        );
      }
      return s === '' ? null : s;
    }
  }
}

/** Formátuje hodnotu pro zobrazení v UI (cs). */
export function formatEquipmentCustomSpecValue(
  field: EquipmentCustomSpecFieldDef,
  value: EquipmentCustomSpecValue | undefined,
): string {
  if (value == null || (typeof value === 'string' && value.trim() === '')) {
    return '—';
  }
  if (field.type === 'boolean') {
    return value === true ? 'Ano' : value === false ? 'Ne' : '—';
  }
  if (field.type === 'date' && typeof value === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (m) return `${m[3]}.${m[2]}.${m[1]}`;
  }
  return String(value);
}
